// StaffAssessmentForm.jsx
import React, { useState, useEffect } from 'react';

const criteriaList = [
  "Appearance and Posture (dress, sitting posture)",
  "Self-expression ability (expression, language)",
  "Behavior and Response Etiquette",
  "Oral expression ability (communication skills)",
  "Comprehensive capability analysis",
  "Enthusiasm and dedication to work",
  "Physical strength and energy",
  "Adaptability",
  "Attitudes and career aspirations consistency",
  "Does the expertise meet the job requirement?",
  "Is the life and work experience rich?",
  "Whether the education level meets the requirement",
  "Whether the temperament and personality fit",
  "Whether the willingness to work is consistent",
  "Whether the work experience meets the requirement",
  "Relevant professional knowledge support",
  "Adaptability to the new working environment",
  "Stability and perseverance in new jobs",
  "Personal ideals are consistent with the enterprise",
  "Whether the required remuneration and welfare are acceptable",
  "Can the potential be brought into play in the position",
  "Whether the overall quality meets the job requirement",
  "Acceptance of job shifts",
  "Distance between residential address and workplace"
];

const StaffAssessmentForm = ({ applicant, existingAssessment, onClose, onSaved, BASE_URL }) => {
  const [formData, setFormData] = useState({
    interviewDate: '',
    salaryRequested: '',
    dateAvailableToStart: '',
    criteriaScores: criteriaList.map((text, i) => ({
      criterionNumber: i + 1,
      criterionText: text,
      score: null,
      notes: ''
    })),
    commentsAndSuggestions: '',
    suggestedEntryDate: ''
  });

  const [loading, setLoading] = useState(false);

  // Safe name extraction
  const getApplicantName = () => {
    if (!applicant) return "Unknown Candidate";

    const app = applicant.applicant?.[0] || applicant.applicant || applicant;
    return `${app?.firstName || ''} ${app?.lastName || ''}`.trim() || "Unnamed Candidate";
  };

  const getPosition = () => applicant?.position || applicant?.job?.jobTitle || "N/A";

  // Load existing assessment if editing
  useEffect(() => {
    if (existingAssessment) {
      setFormData({
        interviewDate: existingAssessment.interviewDate || '',
        salaryRequested: existingAssessment.salaryRequested || '',
        dateAvailableToStart: existingAssessment.dateAvailableToStart || '',
        criteriaScores: existingAssessment.criteriaScores || formData.criteriaScores,
        commentsAndSuggestions: existingAssessment.commentsAndSuggestions || '',
        suggestedEntryDate: existingAssessment.suggestedEntryDate || ''
      });
    }
  }, [existingAssessment]);

  const handleScoreChange = (index, score) => {
    const updated = [...formData.criteriaScores];
    updated[index].score = score;
    setFormData({ ...formData, criteriaScores: updated });
  };

  const handleNotesChange = (index, notes) => {
    const updated = [...formData.criteriaScores];
    updated[index].notes = notes;
    setFormData({ ...formData, criteriaScores: updated });
  };

  const calculateTotal = () => 
    formData.criteriaScores.reduce((sum, item) => sum + (item.score || 0), 0);

  const percentage = Math.round((calculateTotal() / 96) * 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!applicant) return;

    setLoading(true);

    try {
      const payload = {
        applicationId: applicant._id,
        jobId: applicant.job?._id || applicant.job,
        interviewerId: "current-user-id", // Replace with real logged-in user ID from Redux/context
        applicantName: getApplicantName(),
        positionApplied: getPosition(),
        salaryRequested: formData.salaryRequested,
        interviewDate: formData.interviewDate,
        dateAvailableToStart: formData.dateAvailableToStart,
        criteriaScores: formData.criteriaScores,
        commentsAndSuggestions: formData.commentsAndSuggestions,
        suggestedEntryDate: formData.suggestedEntryDate,
        evaluatorName: "Current Evaluator", // Replace with real name
        totalScore: calculateTotal(),
        percentageScore: percentage,
        overallLevel: percentage >= 85 ? 'Excellent' : 
                      percentage >= 70 ? 'Good' : 
                      percentage >= 50 ? 'Fair' : 'Poor'
      };

      const method = existingAssessment ? 'PUT' : 'POST';
      const url = existingAssessment 
        ? `${BASE_URL}/${existingAssessment._id}`
        : `${BASE_URL}/create`;

      const res = await fetch(url, {
        method,
        headers: {    
            'Authorization': `Bearer ${localStorage.getItem('token')}` ,
            'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(existingAssessment ? 'Assessment updated!' : 'Assessment saved successfully!');
        onSaved?.();
        onClose();
      } else {
        alert('Failed to save assessment');
      }
    } catch (err) {
      console.error(err);
      alert('Error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-amber-50">
          <h2 className="text-2xl font-bold text-gray-900">Staff Assessment Form</h2>
          <p className="text-orange-700 mt-1 font-medium">
            {getApplicantName()} — {getPosition()}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-10">
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Interview Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-orange-500"
                value={formData.interviewDate}
                onChange={(e) => setFormData({ ...formData, interviewDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Salary Requested</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-orange-500"
                value={formData.salaryRequested}
                onChange={(e) => setFormData({ ...formData, salaryRequested: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date Available to Start</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-orange-500"
                value={formData.dateAvailableToStart}
                onChange={(e) => setFormData({ ...formData, dateAvailableToStart: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Suggested Entry Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-orange-500"
                value={formData.suggestedEntryDate}
                onChange={(e) => setFormData({ ...formData, suggestedEntryDate: e.target.value })}
              />
            </div>
          </div>

          {/* Evaluation Table */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Interview Evaluation Criteria</h3>
            <div className="overflow-x-auto border rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="p-4 text-left border">S/N</th>
                    <th className="p-4 text-left border">Criteria</th>
                    <th className="p-4 border text-center w-20">Excellent (4)</th>
                    <th className="p-4 border text-center w-20">Good (3)</th>
                    <th className="p-4 border text-center w-20">Fair (2)</th>
                    <th className="p-4 border text-center w-20">Poor (1)</th>
                    <th className="p-4 border">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.criteriaScores.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-4 border font-medium">{item.criterionNumber}</td>
                      <td className="p-4 border">{item.criterionText}</td>
                      {[4, 3, 2, 1].map((score) => (
                        <td key={score} className="p-4 border text-center">
                          <input
                            type="radio"
                            name={`score-${index}`}
                            checked={item.score === score}
                            onChange={() => handleScoreChange(index, score)}
                            className="w-5 h-5 accent-orange-600 cursor-pointer"
                          />
                        </td>
                      ))}
                      <td className="p-4 border">
                        <textarea
                          value={item.notes}
                          onChange={(e) => handleNotesChange(index, e.target.value)}
                          className="w-full h-20 border rounded-lg p-2 text-sm resize-y"
                          placeholder="Add comments..."
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Score Summary */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-2xl border border-orange-100">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <p className="text-lg font-semibold">Total Score: <span className="text-2xl text-orange-600">{calculateTotal()}/96</span></p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{percentage}%</p>
              </div>
              <div className="text-right mt-4 md:mt-0">
                <p className="text-sm text-gray-600">Overall Level</p>
                <p className={`text-3xl font-bold mt-1 ${percentage >= 85 ? 'text-green-600' : percentage >= 70 ? 'text-blue-600' : percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {percentage >= 85 ? 'Excellent' : percentage >= 70 ? 'Good' : percentage >= 50 ? 'Fair' : 'Poor'}
                </p>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium mb-2">Comments and Recruitment Suggestions</label>
            <textarea
              className="w-full h-32 border border-gray-300 rounded-2xl p-4 focus:ring-2 focus:ring-orange-500"
              value={formData.commentsAndSuggestions}
              onChange={(e) => setFormData({ ...formData, commentsAndSuggestions: e.target.value })}
              placeholder="Strengths, weaknesses, overall impression and recommendation..."
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 border border-gray-300 rounded-2xl hover:bg-gray-100 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-4 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-2xl font-semibold transition"
            >
              {loading ? "Saving Assessment..." : existingAssessment ? "Update Assessment" : "Save Assessment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffAssessmentForm;
